import React, { useRef, useEffect, useState, useCallback } from 'react';
import './ScratchCard.css';

// Espace additionnel réservé au titre visible (pousse le cœur vers le bas sans retoucher les
// margin_top déjà calés fond par fond). Le titre est positionné à ~33% de la hauteur de la
// carte (cf. .scratch-card-titre) : ce décalage doit dégager cette zone. À affiner plus tard
// si on regénère des fonds prévus pour un titre.
const TITRE_OFFSET_PX = 220;

const ScratchCard = ({
  width,
  height,
  imageSrc,
  gratteSrc,
  brushSize,
  text,
  titre,
  textSize,
  textColor,
  marginTop,
  lineHeight, 
  textHeight, 
  gratte,
  onScratchProgress,  // Callback avec le pourcentage gratté
  onScratchComplete,  // Callback quand le seuil est atteint
  revealThreshold = 50 // Pourcentage pour considérer "gratté"
}) => {
  const canvasRef = useRef(null);
  const gratteRef = useRef(null);
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [scratchedPercent, setScratchedPercent] = useState(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    const image = gratteRef.current;
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      context.drawImage(image, 0, 0);
      context.globalCompositeOperation = 'destination-out'; // Permet le grattage
    };
  }, [gratteSrc]);

  // Calculer le pourcentage gratté
  const calculateScratchedPercent = useCallback(() => {
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    const pixels = context.getImageData(0, 0, width, height).data;
    const totalPixels = width * height;
    let scratchedPixels = 0;

    for (let i = 0; i < pixels.length; i += 4) {
      if (pixels[i + 3] === 0) {
        scratchedPixels++;
      }
    }

    setScratchedPercent((scratchedPixels / totalPixels) * 100);
  }, [width, height]);

  const handleMouseDown = () => {
    setIsMouseDown(true);
  };

  const handleMouseUp = useCallback(() => {
    if (isMouseDown) {
      setIsMouseDown(false);
      calculateScratchedPercent();
    }
  }, [isMouseDown, calculateScratchedPercent]);

  const handleMouseMove = (e) => {
    if (!isMouseDown) return;
    scratch(e.clientX, e.clientY);
  };

  // Gérer les événements tactiles
  const handleTouchStart = (e) => {
    setIsMouseDown(true);
    const touch = e.touches[0];
    scratch(touch.clientX, touch.clientY);
  };

  const handleTouchMove = (e) => {
    if (!isMouseDown) return;
    const touch = e.touches[0];
    scratch(touch.clientX, touch.clientY);
  };

  const handleTouchEnd = () => {
    if (isMouseDown) {
      setIsMouseDown(false);
      calculateScratchedPercent();
    }
  };

  const scratch = (x, y) => {
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const offsetX = x - rect.left;
    const offsetY = y - rect.top;

    context.beginPath();
    context.arc(offsetX, offsetY, brushSize, 0, Math.PI * 2, true);
    context.fill();

    // Appeler la fonction de gestion du grattage
    handleScratch();
  };

  // Fonction pour calculer le pourcentage gratté
  const calculateScratchPercentage = (ctx, width, height) => {
    const imageData = ctx.getImageData(0, 0, width, height);
    const pixels = imageData.data;
    let transparentPixels = 0;
    
    for (let i = 3; i < pixels.length; i += 4) {
      if (pixels[i] === 0) transparentPixels++;
    }
    
    return (transparentPixels / (pixels.length / 4)) * 100;
  };

  // Appeler cette fonction après chaque mouvement de grattage
  const handleScratch = () => {
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    const percentage = calculateScratchPercentage(context, width, height);
    
    if (onScratchProgress) {
      onScratchProgress(percentage);
    }
    
    if (percentage >= revealThreshold && onScratchComplete) {
      onScratchComplete();
    }
  };

  // Ajuster le marginTop du canvas si "gratte" contient le mot "rond"
  // marginTop vient de l'API (chaîne de caractères, ex. "70") : Number() est indispensable
  // avant tout `+`, sinon "70" + 60 concatène en "7060" au lieu d'additionner en 130.
  const baseAdjustedMarginTop = gratteSrc.includes('rond') ? Number(marginTop) - 30 : Number(marginTop);
  const adjustedMarginTop = titre ? baseAdjustedMarginTop + TITRE_OFFSET_PX : baseAdjustedMarginTop;

  return (
    <div className="scratch-card-container">
      <div
        className="mobile-container"
        style={{ backgroundImage: `url(${imageSrc})` }}  // Utilisation de imageSrc comme fond
      >
        {titre && (
          <div className="scratch-card-titre">
            {titre}
          </div>
        )}
        <div className="scratch-card-canvas-container" style={{ marginTop: `${adjustedMarginTop}px` }}>
          <canvas
            ref={canvasRef}
            width={width}
            height={height}
            className="scratch-card-canvas"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onTouchStart={handleTouchStart}   // Gérer l'interaction tactile
            onTouchMove={handleTouchMove}     // Gérer l'interaction tactile
            onTouchEnd={handleTouchEnd}       // Gérer l'interaction tactile
            style={{ touchAction: 'none' }}   // Empêche le défilement pendant le grattage sur mobile
          />
          <img
            ref={gratteRef}
            src={gratteSrc}
            alt="gratte"
            style={{ display: 'none' }}
          />
        </div>

        <div
          className="scratch-card-text"
          style={{
            fontSize: `${textSize}px`,
            color: textColor,
            marginTop: `${Number(marginTop) + Number(textHeight) + (titre ? TITRE_OFFSET_PX : 0)}px`,
            lineHeight: `${lineHeight}px` // Interligne en pixels
          }}
        >
          {/* Convertir les \n en <br /> et interpréter comme du HTML */}
          <p dangerouslySetInnerHTML={{ __html: text.replace(/\n/g, '<br />') }} />
        </div>
      </div>
    </div>
  );
};

export default ScratchCard;
